import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export interface HeroSlide {
  id: string;
  name: string;
  summary: string;
  categoryId: string;
  categoryName: string;
  heroImage: string;
}

export default function HeroCarousel({ slides }: { slides: HeroSlide[] }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = setInterval(() => {
      setIndex((v) => (v + 1) % slides.length);
    }, 5200);
    return () => clearInterval(timer);
  }, [slides.length]);

  if (slides.length === 0) return null;

  const go = (dir: number) => {
    setIndex((v) => (v + dir + slides.length) % slides.length);
  };

  return (
    <section className="relative w-full rounded-lg overflow-hidden bg-secondary-950">
      <div className="relative h-[320px] md:h-[400px]">
        {slides.map((slide, i) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
          >
            <img
              src={slide.heroImage}
              alt={`${slide.name} 焦点推荐`}
              title={`${slide.name} 黄金投资软件`}
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-secondary-950/95 via-secondary-950/70 to-secondary-950/30" />
          </div>
        ))}

        <div className="relative z-10 h-full flex flex-col justify-center px-6 md:px-10 max-w-2xl text-background-50">
          <span className="self-start px-3 py-1 rounded-full bg-primary-500/90 text-secondary-950 text-xs font-medium mb-3">
            焦点推荐 · {slides[index].categoryName}
          </span>
          <h2 className="font-heading text-2xl md:text-4xl font-bold leading-tight">
            {slides[index].name}
          </h2>
          <p className="mt-3 text-sm md:text-base text-background-200 leading-relaxed max-w-xl">
            {slides[index].summary}
          </p>
          <div className="mt-5 flex items-center gap-3 flex-wrap">
            <Link
              to={`/detail/${slides[index].id}`}
              className="h-11 px-6 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors flex items-center gap-2"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-download-line text-base" />
              </span>
              免费下载
            </Link>
            <Link
              to={`/category/${slides[index].categoryId}`}
              className="h-11 px-6 rounded-md bg-background-50/10 border border-background-50/40 hover:bg-background-50/20 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors flex items-center gap-2"
            >
              查看同类软件
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-base" />
              </span>
            </Link>
          </div>
        </div>

        <button
          type="button"
          onClick={() => go(-1)}
          aria-label="上一个"
          className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-secondary-950/50 hover:bg-secondary-950/80 text-background-50 items-center justify-center cursor-pointer transition-colors"
        >
          <i className="ri-arrow-left-s-line text-xl" />
        </button>
        <button
          type="button"
          onClick={() => go(1)}
          aria-label="下一个"
          className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-secondary-950/50 hover:bg-secondary-950/80 text-background-50 items-center justify-center cursor-pointer transition-colors"
        >
          <i className="ri-arrow-right-s-line text-xl" />
        </button>

        <div className="absolute bottom-4 left-6 md:left-10 z-10 flex items-center gap-2">
          {slides.map((slide, i) => (
            <button
              key={slide.id}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={`切换到第${i + 1}个推荐`}
              className={`h-2 rounded-full cursor-pointer transition-all ${
                i === index ? 'w-6 bg-primary-400' : 'w-2 bg-background-50/50'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}